import { Outlet, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Calendar,
  Users,
  Package,
  Ticket,
  QrCode,
  BarChart3,
  ChevronLeft,
  Menu,
  X,
  Mail,
  UserCheck,
  User,
  Building2,
  CreditCard,
  UsersRound,
  LogOut,
  ClipboardList,
  Activity,
  LifeBuoy
} from "lucide-react";
import { useState, useLayoutEffect } from "react";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";

export const AdminLayout = () => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { admin, logout } = useAuth();

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/bookings", label: "Bookings", icon: Users },
    { path: "/admin/classes", label: "Classes", icon: Calendar },
    { path: "/admin/schedules", label: "Schedules", icon: Calendar },
    { path: "/admin/instructors", label: "Instructors", icon: User },
    { path: "/admin/packages", label: "Packages", icon: Package },
    { path: "/admin/package-management", label: "Package Mgmt", icon: CreditCard },
    { path: "/admin/events", label: "Events", icon: Ticket },
    { path: "/admin/event-roster", label: "Event Roster", icon: ClipboardList },
    { path: "/admin/private-sessions", label: "Private Sessions", icon: UserCheck },
    { path: "/admin/studio-bookings", label: "Studio Bookings", icon: Building2 },
    { path: "/admin/clients", label: "Clients", icon: UsersRound },
    { path: "/admin/attendance", label: "Attendance", icon: QrCode },
    { path: "/admin/class-roster", label: "Class Roster", icon: ClipboardList },
    { path: "/admin/activities", label: "Activities", icon: Activity },
    { path: "/admin/support", label: "Support Tickets", icon: LifeBuoy },
    { path: "/admin/campaigns", label: "Campaigns", icon: Mail },
    { path: "/admin/reports", label: "Reports", icon: BarChart3 },
  ];

  const NavLink = ({ item, onClick }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path || 
      (item.path === "/admin/dashboard" && location.pathname === "/admin");
    
    return (
      <Link
        to={item.path}
        onClick={onClick}
        data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
          isActive 
            ? "bg-empire-gold/10 text-empire-gold" 
            : "text-neutral-400 hover:text-white hover:bg-white/5"
        }`}
      >
        <Icon className="w-5 h-5" />
        <span className="font-medium">{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-neutral-950 border-b border-neutral-900 px-4 py-3">
        <div className="flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <img
              src="/branding/logo-light.png"
              alt="Empire Dance Kenya Admin"
              className="h-12 w-auto"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextElementSibling?.classList.remove("hidden");
              }}
            />
            <span className="hidden font-cookie text-2xl text-empire-gold">
              Empire <span className="text-white text-sm font-semibold">Admin</span>
            </span>
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-white"
            data-testid="mobile-menu-toggle"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/80 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed h-full bg-neutral-950 border-r border-neutral-900 z-50
        transition-transform duration-300 ease-in-out
        lg:translate-x-0 lg:w-64
        ${sidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64'}
        top-0 lg:top-0
        flex flex-col
      `}>
        {/* Logo Area */}
        <div className="p-6 pt-20 lg:pt-6 flex-shrink-0">
          {/* Logo - Hidden on mobile (shown in header) */}
          <Link to="/admin" className="hidden lg:flex items-center gap-2">
            <img
              src="/branding/logo-light.png"
              alt="Empire Dance Kenya Admin"
              className="h-12 w-auto"
              onError={(e) => {
                e.target.style.display = "none";
                e.target.nextElementSibling?.classList.remove("hidden");
              }}
            />
            <span className="hidden font-cookie text-2xl text-empire-gold">
              Empire <span className="text-white text-sm font-semibold">Admin</span>
            </span>
          </Link>
        </div>

        {/* Navigation - Scrollable */}
        <nav className="flex-1 overflow-y-auto px-6 pb-4 space-y-1">
          {navItems.map((item) => (
            <NavLink 
              key={item.path} 
              item={item} 
              onClick={() => setSidebarOpen(false)}
            />
          ))}
        </nav>

        {/* Bottom actions - Fixed at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-neutral-900 bg-neutral-950 space-y-1">
          <Link to="/" onClick={() => setSidebarOpen(false)}>
            <Button variant="ghost" className="w-full justify-start text-neutral-400 hover:text-white">
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back to Site
            </Button>
          </Link>
          <Button
            variant="ghost"
            onClick={logout}
            className="w-full justify-start text-neutral-400 hover:text-red-400"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Outlet />
          </motion.div>
        </div>
      </main>
    </div>
  );
};
